/**
 * Input-mask helpers for TextField `format` props. They run on every
 * keystroke / paste and reject anything that doesn't fit the mask, so the
 * numeric `inputMode` / `keyboardType` hints (which only affect mobile soft
 * keyboards) are backed up on web and hardware-keyboard input.
 */

/** Digits only, no length cap — for fields whose backend expects integers. */
export const formatInteger = (raw: string): string => raw.replace(/\D/g, '');

/** US ZIP code: up to 5 digits. Long format (00000-0000) is a future extension. */
export const formatZipCode = (raw: string): string => raw.replace(/\D/g, '').slice(0, 5);

/** A complete US phone display mask: (xxx) xxx-xxxx. */
const PHONE_DISPLAY_PATTERN = /^\(\d{3}\) \d{3}-\d{4}$/;

/** Max length of the formatted phone display string `(xxx) xxx-xxxx`. */
export const PHONE_DISPLAY_MAX_LENGTH = 14;

/** US phone: digits only (max 10), progressively masked as `(xxx) xxx-xxxx`. */
export const formatPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 10);

  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/** Whether a formatted phone value is empty or a complete `(xxx) xxx-xxxx` mask. */
export const isValidPhoneDisplay = (value: string): boolean =>
  value === '' || PHONE_DISPLAY_PATTERN.test(value);
